import { Prec, type Extension } from '@codemirror/state';
import { keymap, type KeyBinding } from '@codemirror/view';
import {
  insertNewlineAndIndent,
  indentMore,
  indentLess,
} from '@codemirror/commands';

export type { KeyBinding };
// Re-exported so a consuming app can re-bind editing keys without importing
// `@codemirror/*` itself. `indentMore`/`indentLess` matter because the editor's
// basic setup does NOT indent on Tab — it only swallows the key — so an app that
// wants Tab to indent has to bind it explicitly.
export { insertNewlineAndIndent, indentMore, indentLess };

/**
 * PSTUDIO-1433: build a CodeMirror keymap extension that OUTRANKS the editor's
 * built-in bindings, so a consuming app can re-bind keys CodeMirror already
 * claims (Enter, Shift-Enter, …) on a `ps-code-editor` via its `extensions`
 * prop.
 *
 * It lives in the design system rather than in the app for one hard reason:
 * two copies of `@codemirror/state` in a bundle break CodeMirror at runtime
 * (facets are compared by identity). Keeping every `@codemirror/*` dependency
 * declared here — and handing apps a plain helper plus the commands they need —
 * guarantees a single copy. The helper itself is deliberately generic: it knows
 * nothing about what the bindings do.
 */
export function createKeymapExtension(
  bindings: readonly KeyBinding[]
): Extension {
  return Prec.highest(keymap.of([...bindings]));
}
